/**
 * useDocumentSections — Owns the data-layer state AND mutation logic for the PageBuilder.
 *
 * Manages:
 *   - `sections` — the array of page sections
 *   - `pageConfig` — page-level configuration (bg color, width, saved colors)
 *   - `siteSections` — shared site-level sections
 *   - `allSectionTypes` — combined static + dynamic section type definitions
 *
 * Provides:
 *   - `fetchSections` / `fetchSiteSections` — data loading
 *   - `persistSectionContent` — routes to page or site-section API
 *   - `updatePageConfig` — page-level config persistence
 *   - `handleQuickUpdate` — the primary section mutation function (layout actions + property updates)
 *   - Site section CRUD (`createSiteSection`, `updateSiteSection`)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { apiRequest } from '../../../config/api';
import {
  isVersionCompatible,
  stampVersion,
  getSectionVersion,
} from '../../../config/pageBuilderVersion';
import { loadDynamicSections, getAllDynamicSections, getDynamicSection } from '../../../utils/sectionRegistry';
import { arrayMove } from '@dnd-kit/sortable';
import { regenerateTemplateIds } from '../../../utils/templateUtils';
import { getDefaultStyleValue } from '../utils/builderHelpers';
import {
  getComponentAtPath as treeGetComponentAtPath,
  removeComponentAtPath as treeRemoveComponentAtPath,
  cloneWithNewIds as treeCloneWithNewIds,
  insertAfterPath as treeInsertAfterPath,
  swapInParent as treeSwapInParent,
  moveComponent as treeMoveComponent,
  updateComponentInTree as treeUpdateComponentInTree,
} from '../utils/treeUtils';
import { getComponentDefinition } from '../../componentDefinitions';

// Default section types — will be augmented with dynamic sections on init
const INITIAL_SECTION_TYPES = {}; // Passed in from caller (SECTION_TYPES constant)

export default function useDocumentSections({
  page,
  onSave,
  toast,                // { showSuccess, showError, showInfo }
  resetHistory,         // from useBuilderHistory
  setModalState,        // from useBuilderChrome (for incompatible sections modal)
  setSelectedSection,   // from useSelection
  selectedSection,      // from useSelection (read-only, for syncing selection on update)
  setEditingSection,    // from useSelection
  immediatePushToHistory, // from useBuilderHistory
  debouncedPushToHistory, // from useBuilderHistory
  onShowComponentPalette, // callback from PageBuilder chrome (for addSectionDirectly openPicker)
  staticSectionTypes,   // The SECTION_TYPES constant from PageBuilder
}) {
  // -------------------------------------------------------------------------
  // Sections
  // -------------------------------------------------------------------------
  const [sections, setSections] = useState([]);

  // -------------------------------------------------------------------------
  // Page config
  // -------------------------------------------------------------------------
  const [pageConfig, setPageConfig] = useState({
    page_background_color: page?.content?.page_background_color || '#f9fafb',
    section_width: page?.content?.section_width || 'boxed',
    custom_section_width: page?.content?.custom_section_width || '800px',
    section_max_width: page?.content?.section_max_width || '1200px',
    saved_colors: page?.content?.saved_colors || [],
  });

  // Sync pageConfig with page.content changes
  useEffect(() => {
    const newConfig = {
      page_background_color: page?.content?.page_background_color || '#f9fafb',
      section_width: page?.content?.section_width || 'boxed',
      custom_section_width: page?.content?.custom_section_width || '800px',
      section_max_width: page?.content?.section_max_width || '1200px',
      saved_colors: page?.content?.saved_colors || [],
    };
    setPageConfig(newConfig);
  }, [page?.content]);

  // -------------------------------------------------------------------------
  // All section types (static + dynamic)
  // -------------------------------------------------------------------------
  const [allSectionTypes, setAllSectionTypes] = useState(staticSectionTypes || INITIAL_SECTION_TYPES);

  // -------------------------------------------------------------------------
  // Site sections
  // -------------------------------------------------------------------------
  const [siteSections, setSiteSections] = useState([]);
  const siteSectionsRef = useRef([]);

  const fetchSiteSections = useCallback(async (siteId) => {
    if (!siteId) {
      setSiteSections([]);
      siteSectionsRef.current = [];
      return [];
    }
    try {
      const response = await apiRequest(`/site-sections/sites/${siteId}`);
      const data = await response.json();
      const parsedData = (data || []).map((section) => ({
        ...section,
        content:
          typeof section.content === 'string'
            ? JSON.parse(section.content)
            : section.content,
      }));
      setSiteSections(parsedData);
      siteSectionsRef.current = parsedData;
      return parsedData;
    } catch (error) {
      console.error('Failed to fetch site sections:', error);
      setSiteSections([]);
      siteSectionsRef.current = [];
      return [];
    }
  }, []);

  // -------------------------------------------------------------------------
  // Site section CRUD
  // -------------------------------------------------------------------------
  const createSiteSection = useCallback(
    async (sectionData) => {
      if (!page?.site_id) return null;
      try {
        const response = await apiRequest(`/site-sections/sites/${page.site_id}`, {
          method: 'POST',
          body: JSON.stringify(sectionData),
        });
        const data = await response.json();
        await fetchSiteSections(page.site_id);
        return data;
      } catch (error) {
        console.error('Failed to create site section:', error);
        return null;
      }
    },
    [page?.site_id, fetchSiteSections],
  );

  const updateSiteSection = useCallback(
    async (sectionId, updates) => {
      try {
        const response = await apiRequest(`/site-sections/${sectionId}`, {
          method: 'PUT',
          body: JSON.stringify(updates),
        });
        const data = await response.json();
        if (page?.site_id) {
          await fetchSiteSections(page.site_id);
        }
        return data;
      } catch (error) {
        console.error('Failed to update site section:', error);
        return null;
      }
    },
    [page?.site_id, fetchSiteSections],
  );

  // -------------------------------------------------------------------------
  // Persist section content — routes to page or site-section API
  // -------------------------------------------------------------------------
  const persistSectionContent = useCallback(
    async (section, content) => {
      const siteSectionId = section.content?.siteSection_id;

      if (siteSectionId) {
        try {
          await updateSiteSection(siteSectionId, { content });
          return { ok: true, isSiteSection: true };
        } catch (error) {
          console.error('Failed to save site section content:', error);
          return { ok: false, isSiteSection: true, error };
        }
      } else {
        const response = await apiRequest(`/pages/${page.id}/sections/${section.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            section_type: section.section_type,
            content,
            order_index: section.order_index,
          }),
        });
        return { ok: response.ok, isSiteSection: false, response };
      }
    },
    [page?.id, updateSiteSection],
  );

  // -------------------------------------------------------------------------
  // Fetch sections (with site-section resolution + version check)
  // -------------------------------------------------------------------------
  const fetchSections = useCallback(async () => {
    try {
      const response = await apiRequest(`/pages/${page.id}/sections`);
      const data = await response.json();

      // Resolve site section references
      const currentSiteSections = siteSectionsRef.current;
      const resolvedData = data.map((section) => {
        const siteSectionId = section.content?.siteSection_id;
        if (siteSectionId && currentSiteSections.length > 0) {
          const siteSection = currentSiteSections.find((ss) => ss.id === siteSectionId);
          if (siteSection) {
            const siteSectionContent =
              typeof siteSection.content === 'string'
                ? JSON.parse(siteSection.content)
                : siteSection.content;
            return {
              ...section,
              content: { ...siteSectionContent, siteSection_id: siteSectionId },
            };
          }
        }
        return section;
      });

      // Version compatibility check
      const compatible = [];
      const incompatible = [];
      resolvedData.forEach((section) => {
        const version = getSectionVersion(section);
        if (isVersionCompatible(version)) {
          compatible.push(section);
        } else {
          incompatible.push({ ...section, version });
        }
      });

      setSections(compatible);
      if (resetHistory) resetHistory(compatible);

      if (incompatible.length > 0 && setModalState) {
        setModalState((prev) => ({ ...prev, incompatibleSections: incompatible }));
        toast?.showError(
          `${incompatible.length} section(s) are incompatible with the current page builder version and were not loaded.`,
        );
      }

      // Auto-select first section
      if (compatible.length > 0 && setSelectedSection) {
        setSelectedSection((prev) => prev || compatible[0]);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast?.showError('Failed to load sections');
    }
  }, [page?.id, resetHistory, toast, setModalState, setSelectedSection]);

  // -------------------------------------------------------------------------
  // Update page config
  // -------------------------------------------------------------------------
  const updatePageConfig = useCallback(
    async (newConfig) => {
      // Optimistic update
      setPageConfig((prev) => ({ ...prev, ...newConfig }));

      try {
        const response = await apiRequest(`/pages/${page.id}/`, {
          method: 'PUT',
          body: JSON.stringify({ content: { ...page.content, ...newConfig } }),
        });

        if (response.ok) {
          toast?.showSuccess('Page settings updated successfully');
          const updatedPage = await response.json();
          if (onSave) onSave(updatedPage);
        } else {
          const error = await response.json();
          toast?.showError(error.detail || 'Failed to update page settings');
          // Revert
          setPageConfig((prev) => {
            const reverted = { ...prev };
            Object.keys(newConfig).forEach((key) => {
              reverted[key] = page.content?.[key] || prev[key];
            });
            return reverted;
          });
        }
      } catch (error) {
        console.error('Error updating page config:', error);
        toast?.showError('Failed to update page settings');
        setPageConfig((prev) => {
          const reverted = { ...prev };
          Object.keys(newConfig).forEach((key) => {
            reverted[key] = page.content?.[key] || prev[key];
          });
          return reverted;
        });
      }
    },
    [page?.id, page?.content, toast, onSave],
  );

  // -------------------------------------------------------------------------
  // Initialize builder (load dynamic sections + fetch data)
  // -------------------------------------------------------------------------
  const initializeBuilder = useCallback(
    async () => {
      try {
        await loadDynamicSections();
        const dynamicSectionsList = getAllDynamicSections();
        const combinedSections = { ...(staticSectionTypes || {}) };
        dynamicSectionsList.forEach((section) => {
          if (section.id !== 'layout-1') return;
          combinedSections[section.id] = {
            label: section.label,
            icon: section.icon,
            description: section.description,
            defaultContent: section.defaultContent,
            isDynamic: true,
            settings: section.settings,
          };
        });
        setAllSectionTypes(combinedSections);
      } catch (err) {
        console.error('Failed to load dynamic sections:', err);
      }

      // Fetch site sections first so fetchSections can resolve references
      if (page?.site_id) {
        await fetchSiteSections(page.site_id);
      }
      await fetchSections();
    },
    [staticSectionTypes, page?.site_id, fetchSiteSections, fetchSections],
  );

  // -------------------------------------------------------------------------
  // handleQuickUpdate — The primary section mutation function.
  // Handles both layout actions (structural tree mutations) and property
  // updates (component prop changes via the actualColumns indirection).
  // -------------------------------------------------------------------------
  const handleQuickUpdate = useCallback(async (sectionId, updates) => {
    try {
      const sectionToUpdate = sections.find(s => s.id === sectionId);
      if (!sectionToUpdate) return;

      // === LAYOUT ACTION PATH ===
      if (sectionToUpdate.section_type === 'layout-1' && updates.layoutAction) {
        const { layoutAction, componentPath, component } = updates;
        const components = [...(sectionToUpdate.content.components || [])];
        let newComponents = components;
        let newSelectedComponentPath = null;

        switch (layoutAction) {
          case 'removeComponent':
            newComponents = treeRemoveComponentAtPath(components, componentPath);
            break;
          case 'duplicateComponent': {
            const compToDuplicate = component || treeGetComponentAtPath(components, componentPath);
            if (compToDuplicate) {
              const cloned = treeCloneWithNewIds(compToDuplicate);
              newComponents = treeInsertAfterPath(components, componentPath, cloned);
              const pathParts = componentPath.split('.');
              const lastIndex = parseInt(pathParts[pathParts.length - 1]);
              pathParts[pathParts.length - 1] = String(lastIndex + 1);
              newSelectedComponentPath = pathParts.join('.');
            }
            break;
          }
          case 'moveUp':
            newComponents = treeSwapInParent(components, componentPath, 'up');
            break;
          case 'moveDown':
            newComponents = treeSwapInParent(components, componentPath, 'down');
            break;
          case 'addChild': {
            const { parentPath, child, index } = updates;
            if (child) {
              let insertIndex;
              if (parentPath === 'root') {
                newComponents = [...components];
                insertIndex = index ?? newComponents.length;
                newComponents.splice(insertIndex, 0, child);
                newSelectedComponentPath = String(insertIndex);
              } else {
                newComponents = JSON.parse(JSON.stringify(components));
                const pathParts = parentPath.split('.');
                let current = newComponents[parseInt(pathParts[0])];
                for (let i = 1; i < pathParts.length; i += 2) {
                  if (pathParts[i] === 'children') {
                    current = current.children[parseInt(pathParts[i + 1])];
                  }
                }
                if (!current.children) current.children = [];
                insertIndex = index ?? current.children.length;
                current.children.splice(insertIndex, 0, child);
                newSelectedComponentPath = `${parentPath}.children.${insertIndex}`;
              }
            }
            break;
          }
          case 'moveComponent': {
            const { sourcePath, targetParentPath, targetIndex } = updates;
            if (sourcePath !== undefined && targetIndex !== undefined) {
              newComponents = treeMoveComponent(components, sourcePath, targetParentPath, targetIndex);
            }
            break;
          }
          case 'updateColumnSpans': {
            const { rowPath, breakpoint: spanBreakpoint, spans: newSpanValues } = updates;
            if (rowPath && newSpanValues && spanBreakpoint) {
              newComponents = JSON.parse(JSON.stringify(components));
              const rowComp = treeGetComponentAtPath(newComponents, rowPath);
              if (rowComp?.type === 'row' && rowComp.children?.length > 0) {
                rowComp.children.forEach((child, idx) => {
                  if (child.type === 'column' && idx < newSpanValues.length) {
                    const currentSpan = child.props?.span;
                    let responsiveSpan;
                    if (typeof currentSpan === 'object' && currentSpan !== null && currentSpan.base !== undefined) {
                      responsiveSpan = { ...currentSpan };
                    } else {
                      responsiveSpan = { base: currentSpan || 6, tablet: null, desktop: null };
                    }
                    responsiveSpan[spanBreakpoint] = newSpanValues[idx];
                    child.props = { ...child.props, span: responsiveSpan };
                  }
                });
              }
              newSelectedComponentPath = sectionToUpdate.content.selectedComponentPath;
            }
            break;
          }
          case 'addColumnToRow': {
            const { rowPath: addRowPath } = updates;
            if (addRowPath) {
              newComponents = JSON.parse(JSON.stringify(components));
              const rowComp = treeGetComponentAtPath(newComponents, addRowPath);
              if (rowComp?.type === 'row') {
                const newColCount = (rowComp.children?.length || 0) + 1;
                if (newColCount <= 12) {
                  const newColumn = {
                    id: `column-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    type: 'column',
                    props: {
                      span: { base: 1, tablet: null, desktop: null },
                      padding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },
                      margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
                      backgroundColor: 'transparent',
                      gap: 'md',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                      visibility: { base: true, tablet: null, desktop: null },
                    },
                    children: [],
                  };
                  if (!rowComp.children) rowComp.children = [];
                  const getSpanBase = (child) => {
                    const s = child.props?.span;
                    if (typeof s === 'object' && s !== null) return s.base || 6;
                    return s || 6;
                  };
                  let donorIdx = -1;
                  let donorSpan = 0;
                  rowComp.children.forEach((child, idx) => {
                    if (child.type === 'column') {
                      const sb = getSpanBase(child);
                      if (sb > 1 && sb > donorSpan) { donorSpan = sb; donorIdx = idx; }
                    }
                  });
                  if (donorIdx === -1) break;
                  const donor = rowComp.children[donorIdx];
                  const newDonorSpan = donorSpan - 1;
                  if (typeof donor.props?.span === 'object' && donor.props.span !== null) {
                    donor.props.span = { ...donor.props.span, base: newDonorSpan };
                  } else {
                    donor.props = { ...donor.props, span: { base: newDonorSpan, tablet: null, desktop: null } };
                  }
                  rowComp.children.push(newColumn);
                }
              }
              newSelectedComponentPath = sectionToUpdate.content.selectedComponentPath;
            }
            break;
          }
          case 'removeColumnFromRow': {
            const { rowPath: removeRowPath, columnIndex: removeColIdx } = updates;
            if (removeRowPath !== undefined && removeColIdx !== undefined) {
              newComponents = JSON.parse(JSON.stringify(components));
              const rowComp = treeGetComponentAtPath(newComponents, removeRowPath);
              if (rowComp?.type === 'row' && rowComp.children?.length > 1) {
                const colToRemove = rowComp.children[removeColIdx];
                if (colToRemove?.type === 'column') {
                  const removedSpan = colToRemove.props?.span;
                  let removedSpanBase;
                  if (typeof removedSpan === 'object' && removedSpan !== null) { removedSpanBase = removedSpan.base || 6; }
                  else { removedSpanBase = removedSpan || 6; }
                  const adjacentIdx = removeColIdx > 0 ? removeColIdx - 1 : 1;
                  const adjacentCol = rowComp.children[adjacentIdx];
                  if (adjacentCol?.type === 'column') {
                    const adjSpan = adjacentCol.props?.span;
                    let adjSpanBase;
                    if (typeof adjSpan === 'object' && adjSpan !== null) { adjSpanBase = adjSpan.base || 6; }
                    else { adjSpanBase = adjSpan || 6; }
                    const newAdjSpan = adjSpanBase + removedSpanBase;
                    if (typeof adjacentCol.props?.span === 'object' && adjacentCol.props.span !== null) {
                      adjacentCol.props.span = { ...adjacentCol.props.span, base: newAdjSpan };
                    } else {
                      adjacentCol.props = { ...adjacentCol.props, span: { base: newAdjSpan, tablet: null, desktop: null } };
                    }
                  }
                  rowComp.children.splice(removeColIdx, 1);
                }
              }
              newSelectedComponentPath = sectionToUpdate.content.selectedComponentPath;
            }
            break;
          }
          case 'convertComponent': {
            const { targetType } = updates;
            const compToConvert = treeGetComponentAtPath(components, componentPath);
            if (compToConvert && targetType) {
              newComponents = JSON.parse(JSON.stringify(components));
              const convertedComp = treeGetComponentAtPath(newComponents, componentPath);
              const oldType = convertedComp.type;
              convertedComp.type = targetType;
              convertedComp.id = `${targetType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              const oldProps = convertedComp.props || {};
              const sourceDef = getComponentDefinition(oldType);
              const sourceDefaults = sourceDef?.defaultProps || {};
              const newProps = {};
              const sharedKeys = [
                'display', 'gap', 'justifyContent', 'flexWrap',
                'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight',
                'padding', 'margin', 'backgroundColor', 'opacity',
                'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
                'overflow', 'visibility', 'position', 'top', 'right', 'bottom', 'left', 'zIndex',
                'customCSS', 'flexChild',
              ];
              for (const key of sharedKeys) {
                const val = oldProps[key] !== undefined ? oldProps[key] : sourceDefaults[key];
                if (val !== undefined) newProps[key] = val;
              }
              if (oldType === 'container' && targetType === 'block') {
                const fd = oldProps.flexDirection || sourceDefaults.flexDirection || 'column';
                const mapped = (fd === 'row' || fd === 'row-reverse') ? 'horizontal' : 'vertical';
                newProps.direction = { base: mapped, tablet: null, desktop: null };
                newProps.alignment = oldProps.alignItems !== undefined ? oldProps.alignItems : (sourceDefaults.alignItems || 'stretch');
                newProps.backgroundImageUrl = oldProps.backgroundImageUrl || '';
                newProps.backgroundSize = oldProps.backgroundSize || 'cover';
                newProps.backgroundPosition = oldProps.backgroundPosition || 'center center';
                newProps.backgroundRepeat = oldProps.backgroundRepeat || 'no-repeat';
              } else if (oldType === 'block' && targetType === 'container') {
                const dir = oldProps.direction || sourceDefaults.direction;
                const baseDir = (typeof dir === 'object' && dir !== null) ? (dir.base || 'vertical') : (dir || 'vertical');
                newProps.flexDirection = (baseDir === 'horizontal') ? 'row' : 'column';
                newProps.alignItems = oldProps.alignment !== undefined ? oldProps.alignment : (sourceDefaults.alignment || 'stretch');
              }
              convertedComp.props = newProps;
              newSelectedComponentPath = componentPath;
            }
            break;
          }
          default:
            break;
        }

        // Update with new components
        const updatedSection = {
          ...sectionToUpdate,
          content: {
            ...sectionToUpdate.content,
            components: newComponents,
            selectedComponentPath: newSelectedComponentPath,
          },
        };

        // Optimistic update
        setSections(prev => prev.map(s => s.id === sectionId ? updatedSection : s));
        // Immediate history push for structural changes
        if (immediatePushToHistory) {
          immediatePushToHistory(sections.map(s => s.id === sectionId ? updatedSection : s));
        }
        if (selectedSection?.id === sectionId) {
          setSelectedSection(updatedSection);
        }

        // Persist to API
        const result = await persistSectionContent(updatedSection, updatedSection.content);
        if (!result.ok) {
          setSections(prev => prev.map(s => s.id === sectionId ? sectionToUpdate : s));
          if (selectedSection?.id === sectionId) setSelectedSection(sectionToUpdate);
          toast?.showError(result.isSiteSection ? 'Failed to update site section' : 'Failed to update section');
        }
        return;
      }

      // === PROPERTY UPDATE PATH ===
      let processedUpdates = { ...updates };

      if (sectionToUpdate.section_type === 'layout-1' && updates.actualColumns) {
        const selectedPath = updates.selectedComponentPath || sectionToUpdate.content.selectedComponentPath;
        if (selectedPath && updates.actualColumns.length > 0) {
          const updatedProps = updates.actualColumns[0];
          let updatedComponents = [...(sectionToUpdate.content.components || [])];
          updatedComponents = treeUpdateComponentInTree(updatedComponents, selectedPath, updatedProps);

          // columnLayout → span mapping
          if ('columnLayout' in updatedProps) {
            const columnLayoutToSpans = {
              'equal': null, '1-2': [4, 8], '2-1': [8, 4], '1-3': [3, 9], '3-1': [9, 3],
              '1-1-1': [4, 4, 4], '1-2-1': [3, 6, 3], 'stacked': [12], 'custom': null,
            };
            const rowComponent = treeGetComponentAtPath(updatedComponents, selectedPath);
            if (rowComponent?.type === 'row' && rowComponent.children?.length > 0) {
              const numChildren = rowComponent.children.length;
              const newColumnLayout = updatedProps.columnLayout;
              const getSpansForLayout = (layoutValue) => {
                if (!layoutValue || layoutValue === 'custom') return null;
                if (layoutValue === 'equal') return Array(numChildren).fill(Math.floor(12 / numChildren));
                if (layoutValue === 'stacked') return Array(numChildren).fill(12);
                return columnLayoutToSpans[layoutValue] || null;
              };
              if (typeof newColumnLayout === 'object' && newColumnLayout !== null) {
                ['base', 'tablet', 'desktop'].forEach((bp) => {
                  const layoutValue = newColumnLayout[bp];
                  if (layoutValue !== null && layoutValue !== undefined) {
                    const effectiveSpans = getSpansForLayout(layoutValue);
                    if (effectiveSpans?.length > 0) {
                      rowComponent.children.forEach((child, index) => {
                        if (child.type === 'column' && index < effectiveSpans.length) {
                          const currentSpan = child.props?.span;
                          let responsiveSpan;
                          if (typeof currentSpan === 'object' && currentSpan !== null && currentSpan.base !== undefined) {
                            responsiveSpan = { ...currentSpan };
                          } else {
                            responsiveSpan = { base: currentSpan || 6, tablet: null, desktop: null };
                          }
                          responsiveSpan[bp] = effectiveSpans[index];
                          child.props = { ...child.props, span: responsiveSpan };
                        }
                      });
                    }
                  }
                });
              } else if (typeof newColumnLayout === 'string') {
                const effectiveSpans = getSpansForLayout(newColumnLayout);
                if (effectiveSpans?.length > 0) {
                  rowComponent.children.forEach((child, index) => {
                    if (child.type === 'column' && index < effectiveSpans.length) {
                      const currentSpan = child.props?.span;
                      let responsiveSpan;
                      if (typeof currentSpan === 'object' && currentSpan !== null && currentSpan.base !== undefined) {
                        responsiveSpan = { ...currentSpan, base: effectiveSpans[index] };
                      } else {
                        responsiveSpan = { base: effectiveSpans[index], tablet: null, desktop: null };
                      }
                      child.props = { ...child.props, span: responsiveSpan };
                    }
                  });
                }
              }
            }
          }

          processedUpdates = { ...updates, components: updatedComponents };
          delete processedUpdates.actualColumns;
        } else {
          delete processedUpdates.actualColumns;
        }
      }

      const updatedSection = {
        ...sectionToUpdate,
        content: { ...sectionToUpdate.content, ...processedUpdates },
      };

      // Detect pure selection changes — skip history and API persistence
      const updateKeys = Object.keys(processedUpdates);
      const isSelectionOnly = updateKeys.length === 1 && updateKeys[0] === 'selectedComponentPath';

      // Optimistic update with cross-section selection clearing
      setSections(prev => prev.map(s => {
        if (s.id === sectionId) return updatedSection;
        if (processedUpdates.selectedComponentPath && s.content?.selectedComponentPath) {
          return { ...s, content: { ...s.content, selectedComponentPath: null } };
        }
        return s;
      }));
      // Debounced history push for property changes (skip for selection-only)
      if (!isSelectionOnly && debouncedPushToHistory) {
        debouncedPushToHistory(sections.map(s => s.id === sectionId ? updatedSection : s));
      }
      if (selectedSection?.id === sectionId) {
        setSelectedSection(updatedSection);
      }

      // Persist to API (skip for selection-only changes)
      if (!isSelectionOnly) {
        const result = await persistSectionContent(updatedSection, updatedSection.content);
        if (!result.ok) {
          toast?.showError(result.isSiteSection ? 'Failed to update site section' : 'Failed to update section');
          setSections(prev => prev.map(s => s.id === sectionId ? sectionToUpdate : s));
          if (selectedSection?.id === sectionId) setSelectedSection(sectionToUpdate);
        }
      }
    } catch (error) {
      console.error('[handleQuickUpdate] Exception:', error);
      toast?.showError('Failed to update section');
    }
  }, [sections, selectedSection, persistSectionContent, immediatePushToHistory, debouncedPushToHistory, toast, setSelectedSection, setSections]);

  // -------------------------------------------------------------------------
  // updateComponentProp — THE HOT PATH
  // Direct component prop update without the actualColumns indirection.
  // Usage: doc.updateComponentProp(sectionId, componentPath, { fontSize: '18px' })
  // -------------------------------------------------------------------------
  const updateComponentProp = useCallback(async (sectionId, componentPath, propUpdates, options = {}) => {
    const { skipHistory = false, skipPersist = false } = options;
    try {
      const sectionToUpdate = sections.find(s => s.id === sectionId);
      if (!sectionToUpdate || sectionToUpdate.section_type !== 'layout-1') return;
      if (!componentPath || !propUpdates) return;

      let updatedComponents = [...(sectionToUpdate.content.components || [])];
      updatedComponents = treeUpdateComponentInTree(updatedComponents, componentPath, propUpdates);

      // Special columnLayout → span mapping (when updating a row's columnLayout)
      if ('columnLayout' in propUpdates) {
        const columnLayoutToSpans = {
          'equal': null, '1-2': [4, 8], '2-1': [8, 4], '1-3': [3, 9], '3-1': [9, 3],
          '1-1-1': [4, 4, 4], '1-2-1': [3, 6, 3], 'stacked': [12], 'custom': null,
        };
        const rowComponent = treeGetComponentAtPath(updatedComponents, componentPath);
        if (rowComponent?.type === 'row' && rowComponent.children?.length > 0) {
          const numChildren = rowComponent.children.length;
          const newColumnLayout = propUpdates.columnLayout;
          const getSpansForLayout = (layoutValue) => {
            if (!layoutValue || layoutValue === 'custom') return null;
            if (layoutValue === 'equal') return Array(numChildren).fill(Math.floor(12 / numChildren));
            if (layoutValue === 'stacked') return Array(numChildren).fill(12);
            return columnLayoutToSpans[layoutValue] || null;
          };
          if (typeof newColumnLayout === 'object' && newColumnLayout !== null) {
            ['base', 'tablet', 'desktop'].forEach((bp) => {
              const layoutValue = newColumnLayout[bp];
              if (layoutValue !== null && layoutValue !== undefined) {
                const effectiveSpans = getSpansForLayout(layoutValue);
                if (effectiveSpans?.length > 0) {
                  rowComponent.children.forEach((child, index) => {
                    if (child.type === 'column' && index < effectiveSpans.length) {
                      const currentSpan = child.props?.span;
                      let responsiveSpan;
                      if (typeof currentSpan === 'object' && currentSpan !== null && currentSpan.base !== undefined) {
                        responsiveSpan = { ...currentSpan };
                      } else {
                        responsiveSpan = { base: currentSpan || 6, tablet: null, desktop: null };
                      }
                      responsiveSpan[bp] = effectiveSpans[index];
                      child.props = { ...child.props, span: responsiveSpan };
                    }
                  });
                }
              }
            });
          } else if (typeof newColumnLayout === 'string') {
            const effectiveSpans = getSpansForLayout(newColumnLayout);
            if (effectiveSpans?.length > 0) {
              rowComponent.children.forEach((child, index) => {
                if (child.type === 'column' && index < effectiveSpans.length) {
                  const currentSpan = child.props?.span;
                  let responsiveSpan;
                  if (typeof currentSpan === 'object' && currentSpan !== null && currentSpan.base !== undefined) {
                    responsiveSpan = { ...currentSpan, base: effectiveSpans[index] };
                  } else {
                    responsiveSpan = { base: effectiveSpans[index], tablet: null, desktop: null };
                  }
                  child.props = { ...child.props, span: responsiveSpan };
                }
              });
            }
          }
        }
      }

      const updatedSection = {
        ...sectionToUpdate,
        content: { ...sectionToUpdate.content, components: updatedComponents },
      };

      // Optimistic update
      setSections(prev => prev.map(s => s.id === sectionId ? updatedSection : s));
      if (!skipHistory && debouncedPushToHistory) {
        debouncedPushToHistory(sections.map(s => s.id === sectionId ? updatedSection : s));
      }
      if (selectedSection?.id === sectionId) {
        setSelectedSection(updatedSection);
      }

      // Persist to API
      if (!skipPersist) {
        const result = await persistSectionContent(updatedSection, updatedSection.content);
        if (!result.ok) {
          toast?.showError(result.isSiteSection ? 'Failed to update site section' : 'Failed to update section');
          setSections(prev => prev.map(s => s.id === sectionId ? sectionToUpdate : s));
          if (selectedSection?.id === sectionId) setSelectedSection(sectionToUpdate);
        }
      }
    } catch (error) {
      console.error('[updateComponentProp] Exception:', error);
      toast?.showError('Failed to update component');
    }
  }, [sections, selectedSection, persistSectionContent, debouncedPushToHistory, toast, setSelectedSection, setSections]);

  // -------------------------------------------------------------------------
  // Section CRUD handlers
  // -------------------------------------------------------------------------

  const handleUpdateSection = useCallback(async (sectionId, updatedSection) => {
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1) return;
    const newSections = [...sections];
    newSections[sectionIndex] = updatedSection;
    setSections(newSections);
    if (immediatePushToHistory) immediatePushToHistory(newSections);
    if (selectedSection?.id === sectionId) setSelectedSection(updatedSection);
    try {
      const response = await apiRequest(`/pages/${page.id}/sections/${sectionId}`, {
        method: 'PUT',
        body: JSON.stringify({ section_type: updatedSection.section_type, content: updatedSection.content, order_index: updatedSection.order_index }),
      });
      if (!response.ok) { toast?.showError('Failed to update section'); fetchSections(); }
    } catch (error) {
      console.error('Error updating section:', error);
      toast?.showError('Failed to update section');
      fetchSections();
    }
  }, [sections, selectedSection, page?.id, toast, fetchSections, immediatePushToHistory, setSelectedSection, setSections]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const activeIndex = sections.findIndex(s => s.id === active.id);
      const overIndex = sections.findIndex(s => s.id === over.id);
      const newSections = arrayMove(sections, activeIndex, overIndex);
      setSections(newSections);
      if (immediatePushToHistory) immediatePushToHistory(newSections);
      try {
        await Promise.all(newSections.map((section, index) =>
          apiRequest(`/pages/${page.id}/sections/${section.id}`, { method: 'PUT', body: JSON.stringify({ order_index: index }) })
        ));
      } catch (error) {
        console.error('Error updating section order:', error);
        toast?.showError('Failed to update section order');
        fetchSections();
      }
    }
  }, [sections, page?.id, toast, fetchSections, immediatePushToHistory, setSections]);

  const handleReorderSections = useCallback(async (sectionId, targetIndex) => {
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (currentIndex === -1 || currentIndex === targetIndex) return;
    const adjustedTargetIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
    if (currentIndex === adjustedTargetIndex) return;
    const newSections = arrayMove(sections, currentIndex, adjustedTargetIndex);
    setSections(newSections);
    if (immediatePushToHistory) immediatePushToHistory(newSections);
    try {
      await Promise.all(newSections.map((section, index) =>
        apiRequest(`/pages/${page.id}/sections/${section.id}`, { method: 'PUT', body: JSON.stringify({ order_index: index }) })
      ));
    } catch (error) {
      console.error('Error updating section order:', error);
      toast?.showError('Failed to update section order');
      fetchSections();
    }
  }, [sections, page?.id, toast, fetchSections, immediatePushToHistory, setSections]);

  const handleSaveSection = useCallback(async (sectionData) => {
    try {
      if (sectionData.id) {
        const versionedContent = stampVersion(sectionData.content);
        if (sectionData.content?.siteSection_id) {
          const result = await persistSectionContent(sectionData, versionedContent);
          if (result.ok) {
            toast?.showSuccess('Site section updated successfully');
            fetchSections();
            if (page?.site_id) await fetchSiteSections(page.site_id);
          } else {
            toast?.showError('Failed to update site section');
          }
        } else {
          const response = await apiRequest(`/pages/${page.id}/sections/${sectionData.id}`, {
            method: 'PUT',
            body: JSON.stringify({ section_type: sectionData.section_type, content: versionedContent }),
          });
          if (response.ok) { toast?.showSuccess('Section updated successfully'); fetchSections(); }
          else { toast?.showError('Failed to update section'); }
        }
      } else {
        const response = await apiRequest(`/pages/${page.id}/sections`, {
          method: 'POST',
          body: JSON.stringify({ section_type: sectionData.section_type, content: stampVersion(sectionData.content), order_index: sections.length }),
        });
        if (response.ok) { toast?.showSuccess('Section added successfully'); fetchSections(); }
        else { toast?.showError('Failed to add section'); }
      }
    } catch (error) {
      console.error('Error saving section:', error);
      toast?.showError('Failed to save section');
    }
    if (setEditingSection) setEditingSection(null);
  }, [page?.id, page?.site_id, sections.length, persistSectionContent, fetchSections, fetchSiteSections, toast, setEditingSection]);

  const addSectionDirectly = useCallback(async (sectionType, { openPicker = false, insertAtIndex } = {}) => {
    try {
      const defaultContent = allSectionTypes[sectionType]?.defaultContent || {};
      if (allSectionTypes[sectionType]?.isDynamic) {
        const dynamicSection = getDynamicSection(sectionType);
        if (dynamicSection?.settings?.columnStructure) {
          const testimonialCount = defaultContent.testimonialCount || 3;
          const defaultTestimonials = [];
          for (let i = 0; i < testimonialCount; i++) {
            const testimonial = { id: `testimonial-${Date.now()}-${i}` };
            dynamicSection.settings.columnStructure.fields.forEach(field => {
              testimonial[field.key] = field.default || '';
              if (field.possible_styles) {
                field.possible_styles.forEach(style => {
                  testimonial[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                });
              }
            });
            defaultTestimonials.push(testimonial);
          }
          defaultContent.actualColumns = defaultTestimonials;
        }
      }
      const response = await apiRequest(`/pages/${page.id}/sections`, {
        method: 'POST',
        body: JSON.stringify({ section_type: sectionType, content: defaultContent, order_index: sections.length }),
      });
      if (response.ok) {
        const newSection = await response.json();
        toast?.showSuccess('Section added successfully');
        if (insertAtIndex != null && insertAtIndex < sections.length) {
          const newSections = [...sections];
          newSections.splice(insertAtIndex, 0, newSection);
          setSections(newSections);
          if (immediatePushToHistory) immediatePushToHistory(newSections);
          try {
            await Promise.all(newSections.map((section, index) =>
              apiRequest(`/pages/${page.id}/sections/${section.id}`, { method: 'PUT', body: JSON.stringify({ order_index: index }) })
            ));
          } catch (error) {
            console.error('Error updating section order after insert:', error);
            fetchSections();
          }
          setTimeout(() => { setSelectedSection(newSection); }, 200);
        } else {
          fetchSections();
          setTimeout(() => {
            setSelectedSection(newSection);
            const previewPanel = document.querySelector('.overflow-y-auto.bg-gray-50');
            if (previewPanel) previewPanel.scrollTo({ top: previewPanel.scrollHeight, behavior: 'smooth' });
            if (openPicker && onShowComponentPalette) onShowComponentPalette(newSection.id, null, 0);
          }, 200);
        }
      } else {
        toast?.showError('Failed to add section');
      }
    } catch (error) {
      console.error('Error adding section:', error);
      toast?.showError('Failed to add section');
    }
  }, [allSectionTypes, page?.id, sections, setSections, immediatePushToHistory, fetchSections, setSelectedSection, toast, onShowComponentPalette]);

  const addSiteSection = useCallback(async (siteSection) => {
    try {
      const baseContent = typeof siteSection.content === 'string' ? JSON.parse(siteSection.content) : siteSection.content;
      const content = { ...baseContent, siteSection_id: siteSection.id };
      const response = await apiRequest(`/pages/${page.id}/sections`, {
        method: 'POST',
        body: JSON.stringify({ section_type: siteSection.section_type, content, order_index: sections.length }),
      });
      if (response.ok) {
        const newSection = await response.json();
        toast?.showSuccess(`Site section "${siteSection.name}" added successfully`);
        fetchSections();
        setTimeout(() => {
          setSelectedSection(newSection);
          const previewPanel = document.querySelector('.overflow-y-auto.bg-gray-50');
          if (previewPanel) previewPanel.scrollTo({ top: previewPanel.scrollHeight, behavior: 'smooth' });
        }, 200);
      } else {
        toast?.showError('Failed to add site section');
      }
    } catch (error) {
      console.error('Error adding site section:', error);
      toast?.showError('Failed to add site section');
    }
  }, [page?.id, sections.length, fetchSections, setSelectedSection, toast]);

  const insertTemplate = useCallback(async (template) => {
    try {
      const regeneratedContent = regenerateTemplateIds(template.content);
      const response = await apiRequest(`/pages/${page.id}/sections`, {
        method: 'POST',
        body: JSON.stringify({ section_type: 'layout-1', content: regeneratedContent, order_index: sections.length }),
      });
      if (response.ok) {
        const newSection = await response.json();
        toast?.showSuccess(`Template "${template.name}" added successfully`);
        fetchSections();
        setTimeout(() => {
          setSelectedSection(newSection);
          const previewPanel = document.querySelector('.overflow-y-auto.bg-gray-50');
          if (previewPanel) previewPanel.scrollTo({ top: previewPanel.scrollHeight, behavior: 'smooth' });
        }, 200);
      } else {
        toast?.showError('Failed to insert template');
      }
    } catch (error) {
      console.error('Error inserting template:', error);
      toast?.showError('Failed to insert template');
    }
  }, [page?.id, sections.length, fetchSections, setSelectedSection, toast]);

  const handleImportSection = useCallback(async (sectionDataOrArray) => {
    const sectionList = Array.isArray(sectionDataOrArray) ? sectionDataOrArray : [sectionDataOrArray];
    let successCount = 0;
    let failCount = 0;
    let lastCreatedSection = null;
    for (let i = 0; i < sectionList.length; i++) {
      const sectionData = sectionList[i];
      try {
        let processedContent = sectionData.content;
        if (sectionData.section_type === 'layout-1' && processedContent) {
          processedContent = regenerateTemplateIds(processedContent);
        }
        const response = await apiRequest(`/pages/${page.id}/sections`, {
          method: 'POST',
          body: JSON.stringify({ section_type: sectionData.section_type, content: stampVersion(processedContent), order_index: sections.length + i }),
        });
        if (response.ok) { lastCreatedSection = await response.json(); successCount++; }
        else { failCount++; }
      } catch (error) {
        console.error('Error importing section:', error);
        failCount++;
      }
    }
    if (successCount > 0 && failCount === 0) {
      toast?.showSuccess(successCount === 1 ? 'Section imported successfully' : `${successCount} sections imported successfully`);
    } else if (successCount > 0 && failCount > 0) {
      toast?.showSuccess(`${successCount} imported, ${failCount} failed`);
    } else {
      toast?.showError('Failed to import sections');
    }
    if (successCount > 0) {
      fetchSections();
      if (lastCreatedSection) {
        setTimeout(() => {
          setSelectedSection(lastCreatedSection);
          const previewPanel = document.querySelector('.overflow-y-auto.bg-gray-50');
          if (previewPanel) previewPanel.scrollTo({ top: previewPanel.scrollHeight, behavior: 'smooth' });
        }, 200);
      }
    }
  }, [page?.id, sections.length, fetchSections, setSelectedSection, toast]);

  const handleDeleteSection = useCallback(async (sectionId, { skipConfirmation = false } = {}) => {
    if (!skipConfirmation && !window.confirm('Are you sure you want to delete this section?')) return;
    try {
      const response = await apiRequest(`/pages/${page.id}/sections/${sectionId}`, { method: 'DELETE' });
      if (response.ok) { toast?.showSuccess('Section deleted successfully'); fetchSections(); }
      else { toast?.showError('Failed to delete section'); }
    } catch (error) {
      console.error('Error deleting section:', error);
      toast?.showError('Failed to delete section');
    }
  }, [page?.id, fetchSections, toast]);

  const handleMoveComponent = useCallback(async (sourceSectionId, sourcePath, targetParentPath, targetIndex, targetSectionId) => {
    // Cross-section move
    if (targetSectionId && targetSectionId !== sourceSectionId) {
      const sourceSection = sections.find(s => s.id === sourceSectionId);
      const targetSection = sections.find(s => s.id === targetSectionId);
      if (!sourceSection || sourceSection.section_type !== 'layout-1') return;
      if (!targetSection || targetSection.section_type !== 'layout-1') return;
      const sourceComponents = JSON.parse(JSON.stringify(sourceSection.content.components || []));
      const targetComponents = JSON.parse(JSON.stringify(targetSection.content.components || []));
      const componentToMove = treeGetComponentAtPath(sourceComponents, sourcePath);
      if (!componentToMove) return;
      const componentClone = JSON.parse(JSON.stringify(componentToMove));
      // Remove from source
      const sourceParts = sourcePath.split('.');
      const sourceIndex = parseInt(sourceParts[sourceParts.length - 1]);
      if (sourceParts.length === 1) {
        sourceComponents.splice(sourceIndex, 1);
      } else {
        const parentParts = sourceParts.slice(0, -2);
        let sourceParent = sourceComponents[parseInt(parentParts[0])];
        for (let i = 1; i < parentParts.length; i += 2) {
          if (parentParts[i] === 'children' && sourceParent?.children) sourceParent = sourceParent.children[parseInt(parentParts[i + 1])];
        }
        if (!sourceParent?.children) return;
        sourceParent.children.splice(sourceIndex, 1);
      }
      // Insert into target
      if (targetParentPath === null) {
        targetComponents.splice(targetIndex, 0, componentClone);
      } else {
        const targetParts = targetParentPath.split('.');
        let targetParent = targetComponents[parseInt(targetParts[0])];
        for (let i = 1; i < targetParts.length; i += 2) {
          if (targetParts[i] === 'children' && targetParent?.children) targetParent = targetParent.children[parseInt(targetParts[i + 1])];
        }
        if (!targetParent) return;
        if (!targetParent.children) targetParent.children = [];
        targetParent.children.splice(targetIndex, 0, componentClone);
      }
      const newPath = targetParentPath !== null ? `${targetParentPath}.children.${targetIndex}` : `${targetIndex}`;
      const updatedSourceSection = { ...sourceSection, content: { ...sourceSection.content, components: sourceComponents, selectedComponentPath: null } };
      const updatedTargetSection = { ...targetSection, content: { ...targetSection.content, components: targetComponents, selectedComponentPath: newPath } };
      setSections(prev => prev.map(s => {
        if (s.id === sourceSectionId) return updatedSourceSection;
        if (s.id === targetSectionId) return updatedTargetSection;
        return s;
      }));
      if (immediatePushToHistory) {
        immediatePushToHistory(sections.map(s => {
          if (s.id === sourceSectionId) return updatedSourceSection;
          if (s.id === targetSectionId) return updatedTargetSection;
          return s;
        }));
      }
      setSelectedSection(updatedTargetSection);
      const [sourceResult, targetResult] = await Promise.all([
        persistSectionContent(updatedSourceSection, updatedSourceSection.content),
        persistSectionContent(updatedTargetSection, updatedTargetSection.content),
      ]);
      if (!sourceResult.ok || !targetResult.ok) {
        setSections(prev => prev.map(s => {
          if (s.id === sourceSectionId) return sourceSection;
          if (s.id === targetSectionId) return targetSection;
          return s;
        }));
        setSelectedSection(sourceSection);
        toast?.showError('Failed to move component between sections');
      }
      return;
    }
    // Same-section move
    const section = sections.find(s => s.id === sourceSectionId);
    if (!section || section.section_type !== 'layout-1') return;
    const newPath = targetParentPath !== null ? `${targetParentPath}.children.${targetIndex}` : `${targetIndex}`;
    handleQuickUpdate(sourceSectionId, { layoutAction: 'moveComponent', sourcePath, targetParentPath, targetIndex, selectedComponentPath: newPath });
  }, [sections, setSections, immediatePushToHistory, setSelectedSection, persistSectionContent, toast, handleQuickUpdate]);

  // -------------------------------------------------------------------------
  // Page config helpers
  // -------------------------------------------------------------------------

  const handleSaveColor = useCallback((colorValue, label) => {
    const newColor = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2, 9),
      label: label || 'Untitled',
      value: colorValue,
      createdAt: Date.now(),
    };
    const newSavedColors = [...(pageConfig.saved_colors || []), newColor];
    updatePageConfig({ saved_colors: newSavedColors });
  }, [pageConfig.saved_colors, updatePageConfig]);

  const handleDeleteSavedColor = useCallback((colorId) => {
    const newSavedColors = (pageConfig.saved_colors || []).filter(c => c.id !== colorId);
    updatePageConfig({ saved_colors: newSavedColors });
  }, [pageConfig.saved_colors, updatePageConfig]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------
  return {
    // Sections
    sections,
    setSections,

    // Page config
    pageConfig,
    setPageConfig,
    updatePageConfig,

    // Section types
    allSectionTypes,
    setAllSectionTypes,

    // Site sections
    siteSections,
    setSiteSections,
    siteSectionsRef,
    fetchSiteSections,
    createSiteSection,
    updateSiteSection,

    // Core operations
    fetchSections,
    persistSectionContent,
    initializeBuilder,

    // Section mutation handlers
    updateComponentProp,
    handleQuickUpdate,
    handleUpdateSection,
    handleDragEnd,
    handleReorderSections,
    handleSaveSection,
    addSectionDirectly,
    addSiteSection,
    insertTemplate,
    handleImportSection,
    handleDeleteSection,
    handleMoveComponent,

    // Page config helpers
    handleSaveColor,
    handleDeleteSavedColor,
  };
}
